import type { FC } from 'react';
import React, { useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import Button from '@atlaskit/button/standard-button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { LoadableAfterPaint } from '@confluence/loadable';
import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { WatchContentNudgeSpotlight } from '@confluence/watch-dialog/entry-points/WatchContentNudgeSpotlight.tsx';
import { WatchButtonPlaceholder } from '@confluence/watch-dialog/entry-points/WatchButtonPlaceholder.js';
import { isSafari } from '@confluence/browser-helper';
import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	startWatch: {
		id: 'content-types-header.start.watch',
		defaultMessage: 'Watch',
		description: 'Text for the start watch button in content types header',
	},
	stopWatch: {
		id: 'content-types-header.stop.watch',
		defaultMessage: 'Stop watching',
		description: 'Text for the stop button in content types header',
	},
});

type TriggerProps = {
	isWatching: boolean;
	isSelected: boolean;
	onClick: () => void;
	onMouseOver: () => void;
	triggerProps?: any;
	contentType: string;
	hasRecentlyUnwatchedContent: boolean;
};

export const ContentTypesWatchDialogButton: FC<WrappedComponentProps & TriggerProps> = ({
	isWatching,
	isSelected,
	onClick,
	onMouseOver,
	triggerProps,
	contentType,
	hasRecentlyUnwatchedContent,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleWatchButton = useCallback(async () => {
		onClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'content-watch-button',
				source: 'viewedContentScreen',
				attributes: {
					contentType,
				},
			},
		}).fire();
	}, [onClick, createAnalyticsEvent, contentType]);

	const icon = isWatching ? (
		<EyeOpenFilledIcon label={intl.formatMessage(i18n.stopWatch)} />
	) : (
		<EyeOpenIcon label={intl.formatMessage(i18n.startWatch)} />
	);

	const tooltipText = isWatching
		? intl.formatMessage(i18n.stopWatch)
		: intl.formatMessage(i18n.startWatch);

	const watchShortcutStrings = useShortcutStrings({
		shortcut: isSafari()
			? UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI
			: UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	});

	const onDismissNudgeButtonClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'WatchDialogWatchContentNudge',
				source: 'WatchButton',
			},
		}).fire();
	};

	const onWatchFromNudgeButtonClick = () => {
		onClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'watched',
				actionSubject: 'button',
				actionSubjectId: 'WatchDialogWatchContentNudge',
				source: 'WatchButton',
			},
		}).fire();
	};

	return (
		<SpotlightManager blanketIsTinted={false}>
			<SpotlightTarget name="content-watch-spotlight">
				<Tooltip
					content={
						fg('platform_tooltip_shortcuts_migration_1') ? (
							tooltipText
						) : (
							<ShortcutVisualizer
								shortcut={
									isSafari()
										? UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI
										: UNIVERSAL_WATCH_CONTENT_SHORTCUT
								}
								contentBefore={tooltipText}
								isTooltip
							/>
						)
					}
					shortcut={fg('platform_tooltip_shortcuts_migration_1') ? watchShortcutStrings : undefined}
					position="top"
					hideTooltipOnClick
				>
					<Button
						onClick={handleWatchButton}
						onMouseOver={onMouseOver}
						appearance="subtle"
						iconBefore={icon}
						isSelected={isSelected}
						{...triggerProps}
						data-testId="watch-content-button"
					/>
				</Tooltip>
			</SpotlightTarget>
			{!isWatching && !hasRecentlyUnwatchedContent && (
				<WatchContentNudgeSpotlight
					onWatchFromNudgeButtonClick={onWatchFromNudgeButtonClick}
					onDismissNudgeButtonClick={onDismissNudgeButtonClick}
				/>
			)}
		</SpotlightManager>
	);
};

export const ContentTypesWatchDialogButtonLoader = LoadableAfterPaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentTypesWatchDialogButton" */ './ContentTypesWatchDialogButton.tsx'
			)
		).ContentTypesWatchDialogButton,
	loading: WatchButtonPlaceholder,
});
